import { useCallback, useEffect, useRef, useState } from "react";
import { NavLink, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import Logo from "../assests/rhino.webp";
import "../assests/portal.css";
import HacktonomyPageLoader from "../components/HactonomyPageLoader";
import { getUser, logout } from "../../authService";
import { FORGOT_PASSWORD_API } from "../apis/apis";
import { firstName, initials, memberSinceLabel } from "../components/portalUtils";

const THEME_KEY = "hp-theme";
const readTheme = () => {
  try {
    return localStorage.getItem(THEME_KEY) === "dark" ? "dark" : "light";
  } catch {
    return "light";
  }
};

const PREPARE_PATHS = ["/user-panel/training", "/user-panel/guide", "/user-panel/live-trainings"];

const MasterTemplate = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [theme, setTheme] = useState(readTheme);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuNote, setMenuNote] = useState(null);
  const [prepareOpen, setPrepareOpen] = useState(true);
  const [mobileNav, setMobileNav] = useState(false);
  const menuRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const refreshUser = useCallback(async () => {
    const usr = await getUser();
    setUser(usr);
    return usr;
  }, []);

  useEffect(() => {
    refreshUser().finally(() => setLoading(false));
  }, [refreshUser]);

  // Back after sign-out: a page restored from the browser's back/forward
  // cache would still show old data, so check the session again.
  useEffect(() => {
    const onShow = (e) => {
      if (e.persisted) window.location.reload();
    };
    window.addEventListener("pageshow", onShow);
    return () => window.removeEventListener("pageshow", onShow);
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(THEME_KEY, theme);
    } catch {
      /* storage blocked */
    }
  }, [theme]);

  useEffect(() => {
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  useEffect(() => {
    setMobileNav(false);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  if (loading) return <HacktonomyPageLoader />;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;

  // Exam detail keeps the nav item it was opened from highlighted
  const fromExplore = location.state?.from === "/user-panel/explore-certifications";
  const onDetail = location.pathname.startsWith("/user-panel/dashboard");
  const navClass = (active) => `nav-item${active ? " active" : ""}`;
  const isActive = (path) => location.pathname === path;

  const handleForgot = async () => {
    try {
      await FORGOT_PASSWORD_API(user.email);
      setMenuNote("✓ Reset link sent to your email");
    } catch {
      setMenuNote("Couldn't send the reset link — try again later");
    }
    setTimeout(() => {
      setMenuNote(null);
      setMenuOpen(false);
    }, 2200);
  };

  const since = memberSinceLabel(user.member_since || user.created_at);

  return (
    <div className="hp" data-theme={theme}>
      <div className="layout">
        <div className={`sidebar-backdrop${mobileNav ? " show" : ""}`} onClick={() => setMobileNav(false)} />
        <aside className={`sidebar${mobileNav ? " mobile-open" : ""}`}>
          <div className="side-brand">
            <div className="mark"><img src={Logo} alt="Hacktonomy" /></div>
            <div>
              <div className="name">Hacktonomy</div>
              <div className="sub">Candidate Portal</div>
            </div>
          </div>

          <NavLink to="/user-panel" end className={() => navClass(isActive("/user-panel") || (onDetail && !fromExplore))}>
            <span className="nav-icon">▤</span> My exams
          </NavLink>
          <NavLink
            to="/user-panel/explore-certifications"
            className={() => navClass(isActive("/user-panel/explore-certifications") || (onDetail && fromExplore))}
          >
            <span className="nav-icon">◎</span> Explore our certifications
          </NavLink>

          <div className="nav-section-label">Prepare for exam</div>
          <div className={`nav-group${prepareOpen || PREPARE_PATHS.includes(location.pathname) ? " open" : ""}`}>
            <button type="button" className="nav-item" onClick={() => setPrepareOpen((o) => !o)}>
              <span className="nav-icon">▦</span> Prepare for exam <span className="nav-chevron">›</span>
            </button>
            <div className="nav-sub">
              <NavLink to="/user-panel/training" className={({ isActive: a }) => navClass(a)}>Recorded Modules</NavLink>
              <NavLink to="/user-panel/guide" className={({ isActive: a }) => navClass(a)}>Resources</NavLink>
              <NavLink to="/user-panel/live-trainings" className={({ isActive: a }) => navClass(a)}>Upcoming Live Trainings</NavLink>
            </div>
          </div>

          <div className="nav-section-label">Progress</div>
          <NavLink to="/user-panel/exam-history" className={({ isActive: a }) => navClass(a)}>
            <span className="nav-icon">▦</span> Exam history
          </NavLink>
          <NavLink to="/user-panel/certification" className={({ isActive: a }) => navClass(a)}>
            <span className="nav-icon">✦</span> Certification
          </NavLink>

          <div className="nav-section-label">Account</div>
          <NavLink to="/user-panel/account" className={({ isActive: a }) => navClass(a)}>
            <span className="nav-icon">⚙</span> Account details
          </NavLink>

          <div className="sidebar-foot">
            {user.member_id}
            {since ? ` · Member since ${since}` : ""}
          </div>
        </aside>

        <div className="main">
          <div className="topbar">
            <div className="topbar-left">
              <button type="button" className="mobile-menu-btn" aria-label="Open menu" onClick={() => setMobileNav(true)}>☰</button>
              <div>
                <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
                  <h1>Welcome back, {user.name}</h1>
                  <span className="header-member-id">Member ID: {user.member_id}</span>
                </div>
                <div className="sub">Track your certification journeys, all in one place.</div>
              </div>
            </div>
            <div className="top-right">
              <button type="button" className="theme-toggle" onClick={() => setTheme((t) => (t === "dark" ? "light" : "dark"))}>
                {theme === "dark" ? "☀️ Light" : "🌙 Dark"}
              </button>
              <div className="user-menu-wrap" ref={menuRef}>
                <button type="button" className="userchip" onClick={() => setMenuOpen((o) => !o)} aria-haspopup="menu" aria-expanded={menuOpen}>
                  <div className="avatar">{initials(user.name)}</div>
                  <div>
                    <div className="name" style={{ textTransform: "capitalize" }}>{user.name}</div>
                    <div className="id">{user.member_id}</div>
                  </div>
                  <span className="user-chevron">▾</span>
                </button>
                <div className={`user-menu${menuOpen ? " open" : ""}`} role="menu">
                  {menuNote ? (
                    <div className="user-menu-note">{menuNote}</div>
                  ) : (
                    <>
                      <button type="button" className="user-menu-item" onClick={() => { setMenuOpen(false); navigate("/user-panel/account"); }}>
                        <span className="nav-icon">⚙</span> Account details
                      </button>
                      <button type="button" className="user-menu-item" onClick={handleForgot}>
                        <span className="nav-icon">🔑</span> Forgot password
                      </button>
                      <div className="user-menu-divider" />
                      <button type="button" className="user-menu-item danger" onClick={() => logout()}>
                        <span className="nav-icon">⏻</span> Sign out
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="content">
            <Outlet context={{ user, refreshUser, theme }} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MasterTemplate;
